import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { HandCoins, Users, Sprout, Award } from "lucide-react";

function Counter({ value, prefix = "", suffix = "" }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const target = Number(value) || 0;
            const duration = 1200;
            const startTs = performance.now();
            const step = (now) => {
              const p = Math.min(1, (now - startTs) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setDisplay(Math.floor(eased * target));
              if (p < 1) requestAnimationFrame(step);
              else setDisplay(target);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [value]);
  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {display.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
}

export default function ImpactCounter() {
  const [stats, setStats] = useState({
    total_donations: 0,
    total_amount_inr: 0,
    unique_donors: 0,
    initiatives: 7,
    events: 3,
    volunteers: 0,
  });

  useEffect(() => {
    api.get("/impact").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = [
    { icon: HandCoins, label: "Raised", value: stats.total_amount_inr, prefix: "₹" },
    { icon: Users, label: "Donors", value: Math.max(stats.unique_donors, stats.total_donations) },
    { icon: Sprout, label: "Focus areas", value: stats.initiatives },
    { icon: Award, label: "Events held", value: stats.events },
  ];

  return (
    <section data-testid="impact-counter" className="section-py bg-[#0A2540] text-white">
      <div className="mx-auto max-w-7xl container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-[#D4AF37]">Our impact so far</div>
            <h2 className="mt-3 font-serif-display text-3xl md:text-5xl leading-tight">
              Small acts. Multiplied by kind people. Every day.
            </h2>
          </div>
          <p className="text-white/70 max-w-md text-sm">
            Numbers grow every time a friend of the foundation gives, volunteers,
            or partners with us on the ground.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {cards.map((c) => (
            <div
              key={c.label}
              className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 md:p-8"
              data-testid={`impact-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <c.icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              <div className="mt-6 font-serif-display text-4xl md:text-5xl">
                <Counter value={c.value} prefix={c.prefix || ""} />
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-white/70">
                {c.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
